import { useState, useEffect } from 'react';

export default function AccessibilityControls() {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);

  useEffect(() => {
    // Load saved font size from localStorage
    const savedSize = localStorage.getItem('fontSize');
    if (savedSize) {
      const size = parseInt(savedSize);
      setFontSize(size);
      applyFontSize(size);
    }
  }, []);

  const applyFontSize = (size: number) => {
    document.documentElement.style.fontSize = `${size}%`;
    localStorage.setItem('fontSize', size.toString());
  };

  const increaseFontSize = () => {
    const newSize = Math.min(fontSize + 10, 150);
    setFontSize(newSize);
    applyFontSize(newSize);
  };

  const decreaseFontSize = () => {
    const newSize = Math.max(fontSize - 10, 80);
    setFontSize(newSize);
    applyFontSize(newSize);
  };

  const resetFontSize = () => {
    setFontSize(100);
    applyFontSize(100);
  };

  return (
    <>
      {/* Floating Button - Positioned above the Talk with Us button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-28 right-6 z-50 w-14 h-14 bg-teal-500 hover:bg-teal-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
        aria-label="Accessibility controls"
      >
        <i className="ri-settings-3-line text-2xl"></i>
      </button>

      {/* Control Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 z-40 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="fixed bottom-44 right-6 z-50 bg-white rounded-2xl shadow-2xl p-6 w-80 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">Accessibility</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Font Size Controls */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-3 block">
                  Text Size
                </label>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={decreaseFontSize}
                    disabled={fontSize <= 80}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 text-gray-700 rounded-lg transition-colors"
                    aria-label="Decrease text size"
                  >
                    <i className="ri-subtract-line text-xl"></i>
                  </button>

                  <div className="flex-1 text-center">
                    <div className="text-2xl font-bold text-gray-900">{fontSize}%</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {fontSize === 100 ? 'Default' : fontSize > 100 ? 'Larger' : 'Smaller'}
                    </div>
                  </div>

                  <button
                    onClick={increaseFontSize}
                    disabled={fontSize >= 150}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 text-gray-700 rounded-lg transition-colors"
                    aria-label="Increase text size"
                  >
                    <i className="ri-add-line text-xl"></i>
                  </button>
                </div>

                {/* Visual Scale */}
                <div className="mt-4 relative">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-teal-400 to-teal-500 transition-all duration-300"
                      style={{ width: `${((fontSize - 80) / 70) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-2">
                    <span>80%</span>
                    <span>100%</span>
                    <span>150%</span>
                  </div>
                </div>
              </div>

              {/* Reset Button */}
              {fontSize !== 100 && (
                <button
                  onClick={resetFontSize}
                  className="w-full py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <i className="ri-restart-line"></i>
                  Reset to Default
                </button>
              )}

              {/* Preview Text */}
              <div className="pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-2">Preview:</p>
                <p className="text-gray-700 leading-relaxed">
                  This is how your text will look. Adjust the size to your comfort level.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}